<template lang='pug'>
  div(v-if='test')
    v-toolbar(dense)
      v-col(cols='8' no-gutters v-if='test')
        div(@click="showEditNameDescription = !showEditNameDescription").clickable.text-body-2.text-truncate {{test.name}}
        div(@click="showEditNameDescription = !showEditNameDescription").clickable.text-caption.grey--text.text-truncate {{test.description}}&nbsp;
          display-tags(v-model='test.tags' @tag="showEditNameDescription = !showEditNameDescription")
      v-spacer
      bionic-metrics-git-commit(type='test'
        :guid='test.guid'
        :git-name='test.gitName'
        :uncommitted='test.uncommitted'
        v-model='test.code'
        @input='triggerSave'
        @commit='load')
    v-container(fluid)
      v-card.mb-2(v-if='showEditNameDescription')
        v-card-text
          v-text-field(label='Test Name' @input='triggerSave' v-model='test.name' type='text' required)
          v-textarea(outlined label='Description' @input='triggerSave' v-model='test.description' type='text' auto-grow rows='3')
          tags(v-model='test.tags' @input='triggerSave')
      v-card.mb-2
        v-card-title
          .text-h5.font-weight-light Test Definition
          v-row(justify='end' align='start')
            bionic-metrics-run-buttons(v-if='test.quickRunSuite' :test-suite='test.quickRunSuite' :quick-run-test='test' @starting='quickRun')
            v-tooltip(bottom v-if='!test.quickRunSuite')
              template(v-slot:activator='{on}')
                div(v-on='on')
                  v-btn.tight-button(:disabled='true' text icon)
                    v-icon play_arrow
              span Set a Quick Run Suite to use Quick Run
            v-tooltip(bottom)
              template(v-slot:activator='{on}')
                v-btn.mr-1(icon v-on='on' @click='openEditor')
                  v-icon fullscreen
              span Full Screen
        // This is where the code editor lives
        bionic-metrics-monaco-editor.inline-editor(
          v-if='codeReady'
          ref='inlineEditor'
          @input='triggerSave'
          v-model='test.code'
          type='test'
          :line='line'
          :guid='test.guid'
          :gitName='test.gitName')
      v-card.mb-2
        v-card-title.mb-4
          v-col.pa-0(no-gutters)
            .text-h5.font-weight-light Quick Run Information
            .text-caption.font-weight-light
              | On quick-run, the test will be appended as the last test in this suite and it will run with this suite's configuration.
        v-card-text
          v-autocomplete(label='Quick Run Suite'
            @input='triggerSave'
            v-model='test.quickRunSuite'
            :loading='searchSuitesLoading'
            :items='availableTestSuites'
            item-text='name'
            :search-input.sync='searchSuites'
            no-data-text='No Suites Found'
            return-object
            clearable)
            template(v-slot:item='{item}')
              v-list-item-content
                v-list-item-title(v-text='item.name')
                v-list-item-subtitle(v-text='item.description')
      v-card.mb-2
        v-card-title.mb-6
          .text-h5.font-weight-light Options
        v-card-text
          v-slider(label='Retries' @change='triggerSave' v-model='test.retries' max='10' thumb-label='always')
          v-checkbox(@change='triggerSave' v-model='test.hardResetOnRetry' color='primary' label='Reset Browser Session on Retry')
      v-card.mb-2
        v-card-title.mb-6
          .text-h5.font-weight-light References
        table-list(:headers='referenceHeaders'
          :items='testSuites'
          item-key='guid'
          table-only
          :total='testSuitesTotal'
          :hasSelection='false'
          :selectAll='false'
          noData='Not Used in Any Suites')
          template(v-slot:row='{item}')
            td
              router-link(:to='`/testsuite/${item.guid}`') {{item.name}}
            td.text-right
              v-btn(text icon :href='`/testsuite/${item.guid}`')
                v-icon edit
    v-dialog(fullscreen transition='dialog-bottom-transition' v-model='showEditor' max-width='1000px' :overlay='true' scrollable)
      v-card
        v-card-title
          .text-h5.font-weight-light Test Definition
          v-row(justify='end' align='start')
            bionic-metrics-run-buttons(v-if='test.quickRunSuite' :test-suite='test.quickRunSuite' :quick-run-test='test' @starting='quickRun')
            v-btn.ma-0(icon @click='showEditor = false')
              v-icon close
        bionic-metrics-monaco-editor(
          ref='fullEditor'
          v-if='showEditor'
          style='height: 100%; width: 100%'
          @input='triggerSave'
          v-model='test.code'
          type='test'
          :line='line'
          :guid='test.guid'
          :gitName='test.gitName')
    v-dialog(transition='dialog-bottom-transition' v-model='showQuickRun' max-width='800px' fullscreen :overlay='true' scrollable @keydown.esc='showQuickRun = false')
      v-card
        v-card-title
          .text-h5.font-weight-light Quick Run
          v-row(justify='end' align='start')
            bionic-metrics-test-status-indicator(v-if='!isRunning' :status='status')
            bionic-metrics-running-indicator-circular(v-if='isRunning')
            v-btn.ml-1(text icon :to='`/results/${runGuid}`')
              v-icon remove_red_eye
            v-btn.mr-1(text icon @click='showQuickRun = false')
              v-icon close
        bionic-metrics-test-logs(:test='quickRunTest')
    v-dialog(v-model='showGitDialog' max-width='800px' height='500px')
      bionic-metrics-git-history(v-if='test' v-model='test.gitName' @input='triggerSave' type='test' :guid='test.guid' :code='test.code' @revert='revert')
</template>

<script>
import Vue from 'vue';
import _ from 'lodash';
import {mapGetters, mapMutations} from 'vuex';
import Test from '../../api/test';
import TestSuite from '../../api/testSuite';
import FunctionMixin from '../../mixins/function.mixin';
import Notifications from '../../services/notification.service';
import Messages from '../../services/message.service';
import BionicMetricsTestStatusIndicator from '../../components/TestStatusIndicator';
import BionicMetricsRunningIndicator from '../../components/RunningIndicator';
import SaveMixin from '../../mixins/save.mixin';
import BionicMetricsGitHistory from '../../components/GitHistory';
import BionicMetricsRunningIndicatorCircular from '../../components/RunningIndicatorCircular';
import BionicMetricsMonacoEditor from '../../components/MonacoEditor';
import BionicMetricsRunButtons from '../../components/RunButtons';
import TableList from '../../components/TableList';
import Tags from '../../components/Tags';
import DisplayTags from '../../components/DisplayTags';
import BionicMetricsGitCommit from '../../components/GitCommit';
import BionicMetricsTestLogs from '../../components/TestLogs';

export default {
  name: 'BionicMetricsTest',
  props: {
    guid: {
      type: String
    }
  },
  async mounted() {
    await this.load();
  },
  async beforeDestroy() {
    await Messages.unsubscribe(this.runGuid, this.messageListener, this.messageKey);
    await this.saveOutstanding();
  },
  components: {
    BionicMetricsTestLogs,
    DisplayTags,
    Tags,
    TableList,
    BionicMetricsGitCommit,
    BionicMetricsGitHistory,
    BionicMetricsRunButtons,
    BionicMetricsMonacoEditor,
    BionicMetricsRunningIndicatorCircular,
    BionicMetricsRunningIndicator,
    BionicMetricsTestStatusIndicator
  },
  mixins: [FunctionMixin, SaveMixin],
  computed: {
    codeReady() {
      return !this.showEditor && this.test && !_.isUndefined(this.test.code);
    },
    saveObject() {
      return this.test;
    },
    line() {
      const {line} = this.$route.query;
      if (line) return parseInt(line, 10);
      return null;
    },
    quickRunTest() {
      return {logs: this.logs};
    },
    ...mapGetters({
      user: 'getUser',
      account: 'getAccount'
    })
  },
  methods: {
    abort() {
      return this.$router.push('/');
    },
    async load() {
      try {
        this.test = await Test.get(this.guid);
        if (!this.test.tags) this.test.tags = [];

        const title = this.test.gitName || this.test.name;
        document.title = title;
        this.setTitle(title);

        this.messageListener = {message: this.onMessage};
        this.getAvailableTestSuites();
        await Messages.addListener(this.messageListener, this.messageKey);
        this.getReferences();
      } catch (error) {
        console.error(error);
        return this.abort();
      }
    },
    async quickRun({runGuid}) {
      this.logs = [];
      this.showQuickRun = true;
      if (this.runGuid) await Messages.removeChannel(this.runGuid);
      this.runGuid = runGuid;
      await Messages.addChannel(this.runGuid);
      Notifications.toast('Quick Run Started');
    },
    async openEditor() {
      this.showEditor = true;
    },
    async save(test) {
      try {
        const {uncommitted} = await Test.update(test.guid, test);
        Vue.set(this.test, 'uncommitted', uncommitted);
      } catch (error) {
        console.error(`Error saving test definition: ${error.message}`);
        Notifications.errorToast('There was a problem saving the Test', {duration: 5000});
      }
    },
    async getAvailableTestSuites(search) {
      this.searchSuitesLoading = true;
      const projection = {
        name: true,
        description: true,
        guid: true
      };
      const limit = 10;
      const {list} = await TestSuite.list({limit, projection, search});
      this.availableTestSuites = list;
      this.searchSuitesLoading = false;
    },
    async getReferences() {
      const {list, total} = await Test.getReferences(this.guid);
      this.testSuites = list;
      this.testSuitesTotal = total;
    },
    async revert(content) {
      console.log(`Reverting to ${content}`);
      this.$refs.inlineEditor.set(content);
      this.showGitDialog = false;
    },
    updateBreadcrumb() {
      this.setBreadcrumb([{text: 'Tests', to: '/tests'}, {text: this.test.name}]);
    },
    updateStatus(message) {
      this.isRunning = !!message.isRunning;
      this.status = message.status;
    },
    updateLog(message) {
      console.log(message);
      this.logs.push(`${message.log.toString()}`);
    },
    clearDirty() {
      console.log('Save mixin saved and we are no longer dirty');
    },
    to(route) {
      this.$router.push(route);
    },
    onMessage(data) {
      if (_.get(data, 'message.type') === 'status') return this.updateStatus(data.message);
      if (_.get(data, 'message.type') === 'log') return this.updateLog(data.message);
    },
    ...mapMutations({
      setTitle: 'setTitle',
      setBreadcrumb: 'setBreadcrumb'
    })
  },
  watch: {
    'test.name': {
      // eslint-disable-next-line
      handler: function () {
        this.updateBreadcrumb();
      },
      deep: true
    },
    // eslint-disable-next-line
    searchSuites: _.debounce(function (val) {
      return this.getAvailableTestSuites(val);
    }, 250)
  },
  data() {
    return {
      testSuites: [],
      testSuitesTotal: 0,
      runGuid: null,
      test: null,
      messageListener: null,
      messageKey: 'quickrun',
      disableButton: false,
      showGitDialog: false,
      showEditNameDescription: false,
      showEditor: false,
      showQuickRun: false,
      isRunning: false,
      status: 'NO_DATA',
      logs: [],
      searchSuitesLoading: false,
      searchSuites: null,
      availableTestSuites: [],
      referenceHeaders: [
        {text: 'Suite Name', align: 'left', sortable: false, value: 'name'},
        {text: '', align: 'right', sortable: false, value: 'actions'}
      ]
    };
  }
};

</script>

<style scoped>
.inline-editor {
  width: 100%;
  min-height: 60vh;
}

.logs-editor {
  height: 100%;
  white-space: pre;
  overflow: auto;
}
</style>
